import { z } from 'zod';
// BH-3 — extraído de apps/api/src/routes/psicometricos/{liga-respuestas,liga-autosave}.ts
// a un paquete compartido para que apps/aspirantes (y cualquier test de
// contrato) valide contra el MISMO schema que corre en el backend, en vez de
// una copia que puede desincronizarse silenciosamente.
export const NUM_GRUPOS_CLEAVER = 24;
export const NUM_REACTIVOS_LIFO = 18;
export const respuestaGrupoCleaverSchema = z
    .object({
    mas: z.number().int().min(0).max(3),
    menos: z.number().int().min(0).max(3),
})
    .refine((r) => r.mas !== r.menos, { message: 'mas y menos no pueden ser iguales' });
export const respuestasCleaverParcialSchema = z.record(z.string(), respuestaGrupoCleaverSchema);
export const respuestasCleaverCompletoSchema = respuestasCleaverParcialSchema.refine((r) => {
    for (let i = 1; i <= NUM_GRUPOS_CLEAVER; i++) {
        if (!(`grupo_${i}` in r))
            return false;
    }
    return true;
}, { message: `Deben incluirse los ${NUM_GRUPOS_CLEAVER} grupos (grupo_1 a grupo_${NUM_GRUPOS_CLEAVER})` });
// BH-1 — el candidato envía el ranking por POSICIÓN (p1..p4, tal como se
// despliega), nunca por nombre de estilo. El mapeo posición→estilo se resuelve
// en el servidor (lifo-scoring.service.ts), nunca aquí ni en el cliente.
export const posicionLifoSchema = z
    .object({
    p1: z.number().int().min(1).max(4),
    p2: z.number().int().min(1).max(4),
    p3: z.number().int().min(1).max(4),
    p4: z.number().int().min(1).max(4),
})
    .refine((r) => new Set([r.p1, r.p2, r.p3, r.p4]).size === 4, {
    message: 'p1..p4 deben ser una permutación exacta de {1,2,3,4}',
});
export const respuestasLifoParcialSchema = z.record(z.string(), posicionLifoSchema);
export const respuestasLifoCompletoSchema = respuestasLifoParcialSchema.refine((r) => {
    for (let i = 1; i <= NUM_REACTIVOS_LIFO; i++) {
        if (!(`reactivo_${i}` in r))
            return false;
    }
    return true;
}, { message: `Deben incluirse los ${NUM_REACTIVOS_LIFO} reactivos (reactivo_1 a reactivo_${NUM_REACTIVOS_LIFO})` });
export const cleaverRespuestasBodySchema = z.object({
    tipo_examen: z.literal('cleaver'),
    respuestas: respuestasCleaverCompletoSchema,
});
export const cleaverAutosaveBodySchema = z.object({
    tipo_examen: z.literal('cleaver'),
    respuestas: respuestasCleaverParcialSchema,
});
export const lifoRespuestasBodySchema = z.object({
    tipo_examen: z.literal('lifo'),
    respuestas: respuestasLifoCompletoSchema,
});
export const lifoAutosaveBodySchema = z.object({
    tipo_examen: z.literal('lifo'),
    respuestas: respuestasLifoParcialSchema,
});
export const NUM_PREGUNTAS_KOSTICK = 90;
// Kostick — el candidato envía 1 (Primera Frase) o 2 (Segunda Frase) por
// pregunta. A diferencia de LIFO/Cleaver, este valor NO revela el factor de
// personalidad al que suma (esa resolución ocurre en kostick-scoring.service.ts,
// nunca aquí ni en el cliente).
export const respuestaKostickSchema = z.union([z.literal(1), z.literal(2)]);
export const respuestasKostickParcialSchema = z.record(z.string(), respuestaKostickSchema);
export const respuestasKostickCompletoSchema = respuestasKostickParcialSchema.refine((r) => {
    if (Object.keys(r).length !== NUM_PREGUNTAS_KOSTICK)
        return false;
    for (let i = 1; i <= NUM_PREGUNTAS_KOSTICK; i++) {
        if (!(`${i}` in r))
            return false;
    }
    return true;
}, { message: `Deben incluirse las ${NUM_PREGUNTAS_KOSTICK} respuestas (llaves "1" a "${NUM_PREGUNTAS_KOSTICK}")` });
export const kostickRespuestasBodySchema = z.object({
    tipo_examen: z.literal('kostick'),
    respuestas: respuestasKostickCompletoSchema,
});
export const kostickAutosaveBodySchema = z.object({
    tipo_examen: z.literal('kostick'),
    respuestas: respuestasKostickParcialSchema,
});
// Terman — 10 subpruebas ('I'..'X'), cada una con 5 tipos de respuesta posibles.
// La longitud/tipo exacto por subprueba (cuántos ítems, 'simple' vs 'doble', etc.)
// es metadata del motor de cálculo (SUBS en terman-scoring.service.ts) — validarla
// aquí duplicaría esa fuente de verdad. El schema compartido solo valida la forma
// genérica (arreglo de texto, o arreglo de pares {a,b}); la validación estricta de
// longitud/tipo por subprueba vive en normalizarRespuestasTerman() del servicio,
// mismo criterio que Kostick delegó su rango 1-90 a normalizarRespuestasKostick().
export const SUBTEST_IDS_TERMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const respuestaSimpleTermanSchema = z.array(z.string());
export const respuestaDobleTermanSchema = z.array(z.object({ a: z.string(), b: z.string() }));
export const respuestasTermanParcialSchema = z.record(z.string(), z.union([respuestaSimpleTermanSchema, respuestaDobleTermanSchema]));
export const respuestasTermanCompletoSchema = respuestasTermanParcialSchema.refine((r) => SUBTEST_IDS_TERMAN.every((id) => id in r), { message: `Deben incluirse las 10 subpruebas (${SUBTEST_IDS_TERMAN.join(', ')})` });
export const termanRespuestasBodySchema = z.object({
    tipo_examen: z.literal('terman'),
    respuestas: respuestasTermanCompletoSchema,
});
export const termanAutosaveBodySchema = z.object({
    tipo_examen: z.literal('terman'),
    respuestas: respuestasTermanParcialSchema,
});
// POST /liga/:token/respuestas — envío completo, califica el examen.
export const ligaRespuestasBodySchema = z.discriminatedUnion('tipo_examen', [
    cleaverRespuestasBodySchema,
    lifoRespuestasBodySchema,
    kostickRespuestasBodySchema,
    termanRespuestasBodySchema,
]);
// PATCH /liga/:token/autosave — progreso parcial, no califica.
export const ligaAutosaveBodySchema = z.discriminatedUnion('tipo_examen', [
    cleaverAutosaveBodySchema,
    lifoAutosaveBodySchema,
    kostickAutosaveBodySchema,
    termanAutosaveBodySchema,
]);
//# sourceMappingURL=psicometricos.js.map